import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import * as IoIcons from "react-icons/io";
import {
  FaMoneyBillAlt,
  FaRegCalendarAlt,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaUserClock,
  FaThumbsUp,
} from "react-icons/fa";
import { PiSignatureLight } from "react-icons/pi";
import { checkPermission } from "../functions/checkPermission";
import { puedeGenerarCodigosTelegram } from "./GestionAsistencias/TelegramAsistencia";

// Uso de classes de Bootstrap en lugar de styled-components
const Sidebar = ({ usuario }) => {
  const location = useLocation(); // Para detectar la ruta actual

  // Establecer estado inicial de submenús
  const [submenuOpen, setSubmenuOpen] = useState({
    accesos: false,
    usuarios: false,
    documentos: false,
    epps: false,
    ausencias: false,
    manuales: false,
    planillas: false,
    asistencia: false,
    recruiting: false,
    onboarding: false,
    agentes: false,
    capacitaciones: false,
    evaluaciones: false,
    atencion: false,
    contabilidad: false,
    compras: false,
    ventas: false,
    tesoreria: false,
  });
  const submenuKeys = [
    "accesos",
    "usuarios",
    "documentos",
    "epps",
    "ausencias",
    "manuales",
    "planillas",
    "asistencia",
    "recruiting",
    "onboarding",
    "agentes",
    "capacitaciones",
    "evaluaciones",
    "atencion",
    "contabilidad",
    "compras",
    "ventas",
    "tesoreria",
  ];


  // Detectar el perfil del usuario
  const perfil = usuario?.rol || "admin";
  const esPerfilSuperAdmin = perfil === "superAdmin";
  const esPerfilAsistencia = perfil === "asistencia";
  const esPerfilSupervisor = perfil === "supervisor";
  const esPerfilAlmacen = perfil === "almacenero";

  // Verificación de permisos granulares
  const tieneAccesoUsuarios = checkPermission(usuario, "usuarios");
  const tieneAccesoDocumentos = checkPermission(usuario, "documentos");
  const rawPlanillasPerms = (() => {
    const permisos = usuario?.permisos;
    if (!permisos || typeof permisos !== "object" || Array.isArray(permisos)) return null;
    const key = Object.keys(permisos).find((k) => {
      const normalized = String(k || "").trim().toLowerCase();
      return normalized === "planillas" || normalized === "planilla";
    });
    return key ? permisos[key] : null;
  })();
  const tienePlanillasEnPermisos =
    rawPlanillasPerms === true ||
    (Array.isArray(rawPlanillasPerms) && rawPlanillasPerms.length > 0) ||
    (rawPlanillasPerms && typeof rawPlanillasPerms === "object" && Object.values(rawPlanillasPerms).some((v) => v === true));
  const tieneAccesoPlanillas =
    tienePlanillasEnPermisos ||
    checkPermission(usuario, "planillas") ||
    checkPermission(usuario, "planillas", "EMPLEADOS") ||
    checkPermission(usuario, "planillas", "EMPLEADOS_MASIVOS") ||
    checkPermission(usuario, "planillas", "PAGOS") ||
    checkPermission(usuario, "planillas", "LISTA_PLANILLAS") ||
    checkPermission(usuario, "planillas", "PLANILLAS");
  const tieneAccesoAsistencia = checkPermission(usuario, "asistencia");
  const tieneAccesoAusencias = checkPermission(usuario, "ausencias");
  const tieneAccesoEpps = checkPermission(usuario, "epps");
  const tieneAccesoRecruiting = checkPermission(usuario, "recruiting");
  const tieneAccesoOnboarding = checkPermission(usuario, "onboarding");
  const tieneAccesoAgentes = checkPermission(usuario, "agentes");
  const tieneAccesoCapacitaciones = checkPermission(usuario, "capacitaciones");
  const tieneAccesoEvaluaciones = checkPermission(usuario, "evaluaciones");
  const tieneAccesoAtencion = checkPermission(usuario, "atencion");
  const tieneAccesoEmpleadosPlanilla =
    checkPermission(usuario, "planillas", "EMPLEADOS") ||
    checkPermission(usuario, "planillas", "LISTA_PLANILLAS") ||
    checkPermission(usuario, "planillas", "PLANILLAS") ||
    checkPermission(usuario, "planillas");
  const tieneAccesoEmpleadosMasivosPlanilla =
    hasPlanillasPermission("EMPLEADOS_MASIVOS", "EMPLEADOS");
  const tieneAccesoPagosPlanilla =
    hasPlanillasPermission("PAGOS", "EMPLEADOS");
  function hasPlanillasPermission(...processes) {
    if (checkPermission(usuario, "planillas")) return true;
    if (processes.some((p) => checkPermission(usuario, "planillas", p))) return true;

    const permisos = usuario?.permisos;
    if (!permisos || typeof permisos !== "object") return false;
    const moduleKey = Object.keys(permisos).find((k) => {
      const normalized = String(k || "").trim().toLowerCase();
      return normalized === "planillas" || normalized === "planilla";
    });
    if (!moduleKey) return false;
    const modulePerms = permisos[moduleKey];
    if (modulePerms === true) return true;
    if (Array.isArray(modulePerms)) {
      const normalized = modulePerms.map((p) =>
        String(p || "").trim().toUpperCase().replace(/\s+/g, "_").replace(/-/g, "_")
      );
      return processes.some((p) => normalized.includes(String(p || "").trim().toUpperCase()));
    }
    if (modulePerms && typeof modulePerms === "object") {
      return processes.some((p) => modulePerms[p] === true);
    }
    return false;
  }
  const tieneAlguno = (modulo, procesos = []) =>
    procesos.some((proceso) => checkPermission(usuario, modulo, proceso));
  const tienePermisoCompras = (proceso) => checkPermission(usuario, "compras", proceso);
  const tienePermisoVentas = (proceso) => checkPermission(usuario, "ventas", proceso);
  const tienePermisoTesoreria = (proceso) =>
    checkPermission(usuario, "tesoreria", proceso) || checkPermission(usuario, "contabilidad", "TESORERIA");
  const tieneAccesoGestionContable =
    checkPermission(usuario, "contabilidad") ||
    tieneAlguno("contabilidad", ["COMPRAS", "FACTURAS", "PAGOS", "COBROS", "TESORERIA", "VENTAS", "PLANILLAS", "PCGE", "LIBROS", "REPORTES"]) ||
    checkPermission(usuario, "planillas", "CONFIG_CONTABLE") ||
    checkPermission(usuario, "planillas", "CUENTAS_CONTABLES") ||
    checkPermission(usuario, "planillas", "PLANILLAS");
  const tieneAccesoGestionCompras =
    checkPermission(usuario, "compras") ||
    tieneAlguno("compras", ["ORDENES", "PROVEEDORES", "CATALOGO"]);
  const tieneAccesoGestionVentas =
    checkPermission(usuario, "ventas") ||
    tieneAlguno("ventas", ["ORDENES"]);
  const tieneAccesoGestionTesoreria =
    checkPermission(usuario, "tesoreria") ||
    tieneAlguno("tesoreria", ["DASHBOARD", "PAGOS", "COBROS", "DIAS_CALLE", "CONCILIACION"]) ||
    checkPermission(usuario, "contabilidad", "TESORERIA");
  const tienePermisoContabilidad = (proceso) =>
    checkPermission(usuario, "contabilidad") || checkPermission(usuario, "contabilidad", proceso);


  // Función para determinar qué categoría está activa según la ruta
  const getActiveCategory = () => {
    const path = location.pathname;

    if (path === "/") return "inicio";
    if (
      [
        "/coordenadas",
        "/ocurrencias",
        "/reportes-asistencia",
        "/asistencia/aprobaciones",
        "/config-asistencia",
        "/asistenciaPDF", // ✅ Corregido: era "/AsistenciaPDF"
        "/reporte-ingresos",
        "/gestion-transporte",
        "/rol-turnos",
        "/eventos",
      ].includes(path)
    )
      return "asistencia";
    if (
      [
        "/ausencias/dashboard",
        "/ausencias/alertas",
        "/ausencias/impacto-financiero",
        "/ausencias/vacaciones-masivas",
        "/ausencias/vacaciones-detalle",
        "/ausencias/ausencias-detalle",
        "/ausencias/indicadores",
        "/ausencias/ausentes-mes",
        "/ausencias/admin",
        "/ausencias/control-papeletas",
        "/ausencias/calendario",
        "/ausencias/solicitud",
        "/ausencias/incremento-automatico",
      ].includes(path)
    )
      return "ausencias";
    if (
      [
        "/aprobaciones",
        "/permisos-roles",
        "/permisos-roles/usuarios-con-roles",
        "/permisos-por-rol",
      ].includes(path)
    )
      return "accesos";
    if (path === "/consumo-firestore") return "consumo";
    if (
      [
        "/usuarios",
        "/umasivos",
        "/grupos",
        "/grupos-masivos",
        "/usuarios-con-firma",
        "/centro-costos",
        "/asignacion-cecos-masivo",
        "/historial-cambios",
      ].includes(path)
    )
      return "usuarios";
    if (
      [
        "/documentos",
        "/documentos/contratos",
        "/documentos/contratos-plantillas",
        "/masivos",
        "/eliminar-masivo",
        "/cmasivos",
        "/renombrar",
        "/duplicar-documento",
        "/reglamentos",
      ].includes(path)
    )
      // ✅ Agregado "/reglamentos"
      return "documentos";
    if (
      [
        "/catalogo_epps",
        "/catalogo_equipos",
        "/catalogo_herramientas",
        "/estadistica_epps",
        "/prestamos_activos",
        "/activos/entrega",
      ].includes(path) ||
      path.startsWith("/activos/entrega/")
    )
      return "epps";
    if (
      [
        "/vacantes",
        "/vacantes/crear",
        "/CurriculumUpload",
        "/candidatos",
        "/catalogos/areas",
        "/catalogos/perfiles",
      ].includes(path) ||
      path.startsWith("/vacantes/")
    )
      return "recruiting";
    if (
      [
        "/SeguimientoOnboarding",
        "/onboarding",
        "/onboarding/editor-cabecera",
        "/ConfiguracionDocumentos",
        "/ListaDocumentosDetalle",
        "/dj",
        "/solicitud",
      ].includes(path) ||
      path.includes("onboarding")
    )
      return "onboarding";
    if (["/signDetector"].includes(path)) return "documentos";
    if (["/docs"].includes(path)) return "manuales";
    if (
      [
        "/empleados",
        "/pagos",
        "/empleados-masivos",
        "/planillas",
        "/boletas-pago",
        "/proceso-planilla",
        "/config-planilla",
        "/config-regimen",
        "/bonos",
        "/adelantos",
        "/feriados",
        "/ocurrencias/nueva",
      ].includes(path)
    )
      return "planillas";
    if (["/gestion-contable", "/config-contable"].includes(path) || (path.startsWith("/contabilidad") && path !== "/contabilidad/compras")) return "contabilidad";
    if (path.startsWith("/gestion-compras") || path === "/contabilidad/compras") return "compras";
    if (path.startsWith("/gestion-ventas")) return "ventas";
    if (path.startsWith("/gestion-tesoreria")) return "tesoreria";


    if (path.startsWith("/documentos/procesador")) return "agentes";
    if (path === "/documentos/procesador/chat-axis") return "agentes";
    if (path === "/documentos/procesador/chat-axio") return "agentes";
    if (path === "/documentos/procesador/chat-nova") return "agentes";
    if (path.startsWith("/capacitaciones")) return "capacitaciones"; // Agregado para capacitaciones
    if (path.startsWith("/evaluaciones")) return "evaluaciones"; // Agregado para evaluaciones de desempeño
    if (path.startsWith("/atencion")) return "atencion"; // Agregado para Mesa de Ayuda y Libro de Reclamaciones

    return null;
  };

  // Al cargar el componente, abrir automáticamente el submenú según el perfil
  useEffect(() => {
    if (usuario?.rol === "asistencia" || usuario?.rol === "supervisor") {
      setSubmenuOpen((prev) => ({ ...prev, asistencia: true }));
    } else if (usuario?.rol === "almacenero") {
      setSubmenuOpen((prev) => ({ ...prev, epps: true }));
    }
  }, [usuario]);

  // useEffect para abrir automáticamente el submenú de la categoría activa
  useEffect(() => {
    const activeCategory = getActiveCategory();
    if (activeCategory && activeCategory !== "inicio") {
      setSubmenuOpen((prev) => ({ ...prev, [activeCategory]: true }));
    }
  }, [location.pathname]);

  const toggleSubmenu = (name) => {
    setSubmenuOpen((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  const collapseAllMenus = () => {
    setSubmenuOpen((prev) => {
      const next = { ...prev };
      submenuKeys.forEach((key) => {
        next[key] = false;
      });
      return next;
    });
  };
  const expandAllMenus = () => {
    setSubmenuOpen((prev) => {
      const next = { ...prev };
      submenuKeys.forEach((key) => {
        next[key] = true;
      });
      return next;
    });
  };

  // Estilos en línea para elementos que necesitan comportamiento dinámico
  const getSubMenuStyle = (isOpen) => {
    return {
      maxHeight: isOpen ? "2000px" : "0",
      overflow: "hidden",
      transition: "max-height 0.3s ease",
    };
  };

  // Clase activa mejorada para NavLink
  const activeClassName = ({ isActive }) => (isActive ? "active-link" : "");

  // Función mejorada para determinar el destacado de categoría
  const getCategoryHighlight = (category) => {
    const activeCategory = getActiveCategory();

    // Destacar si es la categoría activa o si coincide con el perfil
    if (activeCategory === category) {
      return "rgba(74, 158, 255, 0.2)"; // Color azul para categoría activa
    }

    if (
      (usuario?.rol === "asistencia" || usuario?.rol === "supervisor") &&
      category === "asistencia"
    ) {
      return "rgba(255, 255, 255, 0.15)";
    }
    if (usuario?.rol === "almacenero" && category === "epps") {
      return "rgba(255, 255, 255, 0.15)";
    }
    return "transparent";
  };

  // Función para obtener el estilo del enlace del submenú
  const getSubmenuLinkStyle = (to) => {
    const isActive = location.pathname === to;
    return {
      fontSize: "0.9em",
      borderLeftWidth: "2px",
      transition: "all 0.3s ease",
      background: isActive ? "rgba(74, 158, 255, 0.15)" : "transparent",
      borderLeftColor: isActive ? "#4a9eff" : "transparent",
      color: isActive ? "#ffffff" : "#ffffff80",
      fontWeight: isActive ? "600" : "400",
    };
  };

  return (
    <div
      className="sidebar bg-dark text-white"
      style={{
        width: "280px",
        height: "100vh",
        background: "linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)",
        padding: "20px",
        boxShadow: "2px 0 5px rgba(0, 0, 0, 0.2)",
        overflowY: "auto",
      }}
    >
      <div className="sidebar-header mb-3">
        <div className="sidebar-eyebrow">Panel principal</div>
        <div className="d-flex gap-2 mt-3">
          <button
            type="button"
            className="sidebar-action-btn"
            onClick={expandAllMenus}
            aria-label="Expandir todos los módulos del menú"
          >
            Expandir
          </button>
          <button
            type="button"
            className="sidebar-action-btn"
            onClick={collapseAllMenus}
            aria-label="Colapsar todos los módulos del menú"
          >
            Colapsar
          </button>
        </div>
      </div>

      <ul className="nav flex-column p-0 m-0">
        {/* Inicio - Siempre visible para todos los perfiles */}
        <li className="nav-item mb-2">
          <NavLink
            to="/"
            className={`nav-link d-flex align-items-center py-2 px-3 text-white rounded ${activeClassName}`}
            style={{
              transition: "all 0.3s ease",
              background:
                getActiveCategory() === "inicio"
                  ? "rgba(74, 158, 255, 0.2)"
                  : "transparent",
            }}
          >
            <FaIcons.FaTachometerAlt
              className="me-2"
              style={{ fontSize: "1.2em" }}
            />
            Dashboard
          </NavLink>
        </li>

        {/* RESTO DE MENÚS - SEGÚN PERMISOS */}
        {tieneAccesoUsuarios && (
          <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("usuarios")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("usuarios"),
                  border:
                    getActiveCategory() === "usuarios"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "usuarios"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("usuarios"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaUser
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Usuarios
                </span>
                {submenuOpen.usuarios ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.usuarios)}
              >
                {checkPermission(usuario, "usuarios", "USUARIOS") && (
                  <li>
                    <NavLink
                      to="/usuarios"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/usuarios")}
                    >
                      <FaIcons.FaList className="me-1" /> Lista de Usuarios
                    </NavLink>
                  </li>
                )}
                {/* Ítem de Lista Negra */}
                {checkPermission(usuario, "usuarios", "USUARIOS") && (
                  <li>
                    <NavLink
                      to="/lista-negra"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/lista-negra")}
                    >
                      <FaIcons.FaBan className="me-1 text-danger" /> Lista Negra
                    </NavLink>
                  </li>
                )}
                {tieneAccesoEmpleadosPlanilla && (
                  <li>
                    <NavLink
                      to="/empleados"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/empleados")}
                    >
                      <FaIcons.FaIdCard className="me-1" /> Empleados
                    </NavLink>
                  </li>
                )}
                {tieneAccesoEmpleadosMasivosPlanilla && (
                  <li>
                    <NavLink
                      to="/empleados-masivos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/empleados-masivos")}
                    >
                      <FaIcons.FaFileExcel className="me-1" /> Empleados Masivos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "GRUPOS") && (
                  <li>
                    <NavLink
                      to="/grupos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/grupos")}
                    >
                      <FaIcons.FaUsers className="me-1" /> Grupos de Usuarios
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "GRUPOS_MASIVOS") && (
                  <li>
                    <NavLink
                      to="/grupos-masivos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/grupos-masivos")}
                    >
                      <FaIcons.FaUpload className="me-1" /> Carga Masiva de Grupos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "USUARIOS_MASIVOS") && (
                  <li>
                    <NavLink
                      to="/umasivos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/umasivos")}
                    >
                      <FaIcons.FaUserPlus className="me-1" /> Usuarios Masivos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "RESET_SEGURIDAD") && (
                  <li>
                    <NavLink
                      to="/changepass"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/changepass")}
                    >
                      <FaIcons.FaUnlockAlt className="me-1" /> Reset de Seguridad
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "FIRMAS") && (
                  <li>
                    <NavLink
                      to="/usuarios-con-firma"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/usuarios-con-firma")}
                    >
                      <PiSignatureLight className="me-1" /> Usuarios con Firma
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "CECOS") && (
                  <li>
                    <NavLink
                      to="/centro-costos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/centro-costos")}
                    >
                      <FaIcons.FaBuilding className="me-1" /> Centros de Costo
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "CECOS_MASIVOS") && (
                  <li>
                    <NavLink
                      to="/asignacion-cecos-masivo"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/asignacion-cecos-masivo")}
                    >
                      <FaIcons.FaCloudUploadAlt className="me-1" /> Asignación Masiva CECO
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "ALERTAS") && (
                  <li>
                    <NavLink
                      to="/alertas-vencimientos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/alertas-vencimientos")}
                    >
                      <FaIcons.FaBell className="me-1" /> Alertas de Vencimientos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "usuarios", "CUMPLEANOS") && (
                  <li>
                    <NavLink
                      to="/cumpleanos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/cumpleanos")}
                    >
                      <FaIcons.FaBirthdayCake className="me-1" /> Cumpleanos
                    </NavLink>
                  </li>
                )}
                {/* Historial de Cambios / Auditoría */}
                {(usuario?.rol === "superAdmin" || usuario?.rol === "admin") && (
                  <li>
                    <NavLink
                      to="/historial-cambios"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/historial-cambios")}
                    >
                      <FaIcons.FaHistory className="me-1" /> Historial de Cambios
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Permisos y Roles - Admin/SuperAdmin */}
        {(esPerfilSuperAdmin || perfil === "admin") && (
          <li className="nav-item mb-2">
            <div
              className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
              onClick={() => toggleSubmenu("accesos")}
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                background: getCategoryHighlight("accesos"),
                border:
                  getActiveCategory() === "accesos"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "accesos"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("accesos"))
              }
            >
              <span className="d-flex align-items-center">
                <FaIcons.FaUserShield className="me-2" style={{ fontSize: "1.2em" }} />
                Permisos y Roles
              </span>
              {submenuOpen.accesos ? <FaIcons.FaCaretDown /> : <FaIcons.FaCaretUp />}
            </div>
            <ul
              className="list-unstyled ms-4 mt-1"
              style={getSubMenuStyle(submenuOpen.accesos)}
            >
              <li>
                <NavLink
                  to="/permisos-roles"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/permisos-roles")}
                >
                  <FaIcons.FaUserCog className="me-1" /> Asignar Roles
                </NavLink>
              </li>
              {(esPerfilSuperAdmin || perfil === "admin" || checkPermission(usuario, "usuarios", "USUARIOS_CON_ROLES")) && (
                <li>
                  <NavLink
                    to="/permisos-roles/usuarios-con-roles"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/permisos-roles/usuarios-con-roles")}
                  >
                    <FaIcons.FaUsers className="me-1" /> Usuarios con Roles
                  </NavLink>
                </li>
              )}
              <li>
                <NavLink
                  to="/permisos-por-rol"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/permisos-por-rol")}
                >
                  <FaIcons.FaShieldAlt className="me-1" /> Permisos por Rol
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/aprobaciones"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/aprobaciones")}
                >
                  <FaIcons.FaSitemap className="me-1" /> Niveles de Aprobación
                </NavLink>
              </li>
            </ul>
          </li>
        )}

        {/* Consumo de Firestore - exclusivo del superAdmin */}
        {esPerfilSuperAdmin && (
          <li className="nav-item mb-2">
            <NavLink
              to="/consumo-firestore"
              className={`nav-link d-flex align-items-center py-2 px-3 text-white rounded ${activeClassName}`}
              style={{
                transition: "all 0.3s ease",
                background: getCategoryHighlight("consumo"),
                border:
                  getActiveCategory() === "consumo"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
            >
              <FaIcons.FaChartLine className="me-2" style={{ fontSize: "1.2em" }} />
              Consumo Firestore
            </NavLink>
          </li>
        )}

        {/* Gestión de Documentos */}
        {tieneAccesoDocumentos && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("documentos")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("documentos"),
                  border:
                    getActiveCategory() === "documentos"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "documentos"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("documentos"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaFilePdf
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Documentos
                </span>
                {submenuOpen.documentos ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.documentos)}
              >
                {checkPermission(usuario, "documentos", "DOCUMENTOS") && (
                  <li>
                    <NavLink
                      to="/documentos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos")}
                    >
                      <FaIcons.FaFile className="me-1" /> Documentos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "DESCARGAR_DOCUMENTOS_DOCUMENTAL") && (
                  <li>
                    <NavLink
                      to="/documentos/descargar-documentos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/descargar-documentos")}
                    >
                      <FaIcons.FaFileDownload className="me-1" /> Descargar Documentos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "CONTRATOS_PLANTILLAS") && (
                  <li>
                    <NavLink
                      to="/documentos/contratos-plantillas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/contratos-plantillas")}
                    >
                      <FaIcons.FaFolderOpen className="me-1" /> Contratos Plantillas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "CONTRATOS_GENERADOR") && (
                  <li>
                    <NavLink
                      to="/documentos/contratos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/contratos")}
                    >
                      <FaIcons.FaFileSignature className="me-1" /> Generador Contratos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "ELIMINAR_MASIVO") && (
                  <li>
                    <NavLink
                      to="/eliminar-masivo"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/eliminar-masivo")}
                    >
                      <FaIcons.FaTrash className="me-1" /> Eliminar Documentos
                      Masivos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "MASIVOS") && (
                  <li>
                    <NavLink
                      to="/masivos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/masivos")}
                    >
                      <FaIcons.FaFileExport className="me-1" /> Documentos Masivos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "BIBLIOTECA") && (
                  <li>
                    <NavLink
                      to="/reglamentos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/reglamentos")}
                    >
                      <FaIcons.FaBookOpen className="me-1" /> Biblioteca
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "RENOMBRAR") && (
                  <li>
                    <NavLink
                      to="/renombrar"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/renombrar")}
                    >
                      <FaIcons.FaEdit className="me-1" /> Renombrar Boletas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "DUPLICAR_RUC") && (
                  <li>
                    <NavLink
                      to="/duplicar-documento"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/duplicar-documento")}
                    >
                      <FaIcons.FaCopy className="me-1" /> Duplicar por RUC
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "documentos", "PLANTILLA_FIRMAS") && (
                  <li>
                    <NavLink
                      to="/signDetector"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/signDetector")}
                    >
                      <FaIcons.FaSignature className="me-1" /> Plantilla de firmas
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Gestión de Planillas */}
        {tieneAccesoPlanillas && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("planillas")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("planillas"),
                  border:
                    getActiveCategory() === "planillas"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "planillas"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("planillas"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaMoneyBillAlt
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Planillas
                </span>
                {submenuOpen.planillas ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.planillas)}
              >
                {hasPlanillasPermission("BOLETAS") && (
                  <li>
                    <NavLink
                      to="/boletas-pago"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/boletas-pago")}
                    >
                      <FaIcons.FaReceipt className="me-1" /> Boletas de Pago
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("BONOS") && (
                  <li>
                    <NavLink
                      to="/bonos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/bonos")}
                    >
                      <FaIcons.FaGift className="me-1" /> Bonos
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("ADELANTOS") && (
                  <li>
                    <NavLink
                      to="/adelantos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/adelantos")}
                    >
                      <FaIcons.FaMoneyBillWave className="me-1" /> Adelantos de Salario
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("FERIADOS") && (
                  <li>
                    <NavLink
                      to="/feriados"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/feriados")}
                    >
                      <FaRegCalendarAlt className="me-1" /> Feriados
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("CONFIGURACION") && (
                  <li>
                    <NavLink
                      to="/config-planilla"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/config-planilla")}
                    >
                      <FaIcons.FaCog className="me-1" /> Configuración
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("REGIMEN_LABORAL", "REGIMEN", "CONFIG_REGIMEN") && (
                  <li>
                    <NavLink
                      to="/config-regimen"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/config-regimen")}
                    >
                      <FaIcons.FaCogs className="me-1" /> Regimen Laboral
                    </NavLink>
                  </li>
                )}
                {tieneAccesoEmpleadosPlanilla && (
                  <li>
                    <NavLink
                      to="/empleados"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/empleados")}
                    >
                      <FaIcons.FaIdCard className="me-1" /> Empleados
                    </NavLink>
                  </li>
                )}
                {tieneAccesoEmpleadosMasivosPlanilla && (
                  <li>
                    <NavLink
                      to="/empleados-masivos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/empleados-masivos")}
                    >
                      <FaIcons.FaFileExcel className="me-1" /> Empleados Masivos
                    </NavLink>
                  </li>
                )}
                {tieneAccesoPagosPlanilla && (
                  <li>
                    <NavLink
                      to="/pagos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/pagos")}
                    >
                      <FaIcons.FaMoneyCheckAlt className="me-1" /> Pagos
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("LISTA_PLANILLAS", "PLANILLAS") && (
                  <li>
                    <NavLink
                      to="/planillas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/planillas")}
                    >
                      <FaIcons.FaList className="me-1" /> Lista de Planillas
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("OCURRENCIAS", "NUEVA_OCURRENCIA") && (
                  <li>
                    <NavLink
                      to="/ocurrencias"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ocurrencias")}
                    >
                      <FaCalendarCheck className="me-1" /> Gestión de Ocurrencias
                    </NavLink>
                  </li>
                )}
                {hasPlanillasPermission("EXPORTACION") && (
                  <li>
                    <NavLink
                      to="/planillas/config-pdt-plame"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/planillas/config-pdt-plame")}
                    >
                      <FaIcons.FaFileCode className="me-1" /> Config PDT PLAME
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {tieneAccesoGestionCompras && (
          <li className="nav-item mb-2">
            <div
              className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded cursor-pointer"
              onClick={() => toggleSubmenu("compras")}
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                background: getCategoryHighlight("compras"),
                border:
                  getActiveCategory() === "compras"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "compras"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("compras"))
              }
            >
              <span className="d-flex align-items-center">
                <FaIcons.FaShoppingCart
                  className="me-2"
                  style={{ fontSize: "1.2em" }}
                />
                Gestión de Compras
              </span>
              {submenuOpen.compras ? (
                <FaIcons.FaCaretDown />
              ) : (
                <FaIcons.FaCaretUp />
              )}
            </div>
            <ul
              className="list-unstyled ms-4 mt-1"
              style={getSubMenuStyle(submenuOpen.compras)}
            >
              {tienePermisoCompras("ORDENES") && (
                <li>
                  <NavLink
                    to="/gestion-compras/ordenes"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-compras/ordenes")}
                  >
                    <FaIcons.FaListUl className="me-1" /> Órdenes de Compra
                  </NavLink>
                </li>
              )}
              {tienePermisoCompras("PROVEEDORES") && (
                <li>
                  <NavLink
                    to="/gestion-compras/proveedores"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-compras/proveedores")}
                  >
                    <FaIcons.FaHandshake className="me-1" /> Proveedores
                  </NavLink>
                </li>
              )}
              {tienePermisoCompras("CATALOGO") && (
                <li>
                  <NavLink
                    to="/gestion-compras/catalogo"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-compras/catalogo")}
                  >
                    <FaIcons.FaBoxes className="me-1" /> Catálogo de Ítems
                  </NavLink>
                </li>
              )}
            </ul>

          </li>
        )}

        {tieneAccesoGestionVentas && (
          <li className="nav-item mb-2">
            <div
              className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded cursor-pointer"
              onClick={() => toggleSubmenu("ventas")}
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                background: getCategoryHighlight("ventas"),
                border:
                  getActiveCategory() === "ventas"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "ventas"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("ventas"))
              }
            >
              <span className="d-flex align-items-center">
                <FaIcons.FaShoppingBag
                  className="me-2"
                  style={{ fontSize: "1.2em" }}
                />
                Gestión de Ventas
              </span>
              {submenuOpen.ventas ? (
                <FaIcons.FaCaretDown />
              ) : (
                <FaIcons.FaCaretUp />
              )}
            </div>
            <ul
              className="list-unstyled ms-4 mt-1"
              style={getSubMenuStyle(submenuOpen.ventas)}
            >
              {tienePermisoVentas("ORDENES") && (
                <li>
                  <NavLink
                    to="/gestion-ventas/ordenes"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-ventas/ordenes")}
                  >
                    <FaIcons.FaClipboardList className="me-1" /> Órdenes de Venta
                  </NavLink>
                </li>
              )}
            </ul>

          </li>
        )}

        {tieneAccesoGestionTesoreria && (
          <li className="nav-item mb-2">
            <div
              className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded cursor-pointer"
              onClick={() => toggleSubmenu("tesoreria")}
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                background: getCategoryHighlight("tesoreria"),
                border:
                  getActiveCategory() === "tesoreria"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "tesoreria"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("tesoreria"))
              }
            >
              <span className="d-flex align-items-center">
                <FaIcons.FaCashRegister
                  className="me-2"
                  style={{ fontSize: "1.2em" }}
                />
                Gestión de Tesorería
              </span>
              {submenuOpen.tesoreria ? (
                <FaIcons.FaCaretDown />
              ) : (
                <FaIcons.FaCaretUp />
              )}
            </div>
            <ul
              className="list-unstyled ms-4 mt-1"
              style={getSubMenuStyle(submenuOpen.tesoreria)}
            >
              {(tienePermisoTesoreria("DASHBOARD") || checkPermission(usuario, "tesoreria") || checkPermission(usuario, "contabilidad", "TESORERIA")) && (
                <li>
                  <NavLink
                    to="/gestion-tesoreria/dashboard"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-tesoreria/dashboard")}
                  >
                    <FaIcons.FaChartPie className="me-1" /> Resumen
                  </NavLink>
                </li>
              )}
              {tienePermisoTesoreria("PAGOS") && (
                <li>
                  <NavLink
                    to="/gestion-tesoreria/pagos"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-tesoreria/pagos")}
                  >
                    <FaIcons.FaArrowCircleUp className="me-1" /> Pagos
                  </NavLink>
                </li>
              )}
              {tienePermisoTesoreria("COBROS") && (
                <li>
                  <NavLink
                    to="/gestion-tesoreria/cobros"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-tesoreria/cobros")}
                  >
                    <FaIcons.FaArrowCircleDown className="me-1" /> Cobros
                  </NavLink>
                </li>
              )}
              {tienePermisoTesoreria("DIAS_CALLE") && (
                <li>
                  <NavLink
                    to="/gestion-tesoreria/dias-calle"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-tesoreria/dias-calle")}
                  >
                    <FaIcons.FaClock className="me-1" /> Días en la Calle
                  </NavLink>
                </li>
              )}
              {(tienePermisoTesoreria("CONCILIACION") || checkPermission(usuario, "tesoreria") || checkPermission(usuario, "contabilidad", "TESORERIA")) && (
                <li>
                  <NavLink
                    to="/gestion-tesoreria/conciliacion"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/gestion-tesoreria/conciliacion")}
                  >
                    <FaIcons.FaExchangeAlt className="me-1" /> Conciliación
                  </NavLink>
                </li>
              )}
            </ul>

          </li>
        )}
        {tieneAccesoGestionContable && (
          <li className="nav-item mb-2">
            <div
              className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded cursor-pointer"
              onClick={() => toggleSubmenu("contabilidad")}
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                background: getCategoryHighlight("contabilidad"),
                border:
                  getActiveCategory() === "contabilidad"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "contabilidad"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("contabilidad"))
              }
            >
              <span className="d-flex align-items-center">
                <FaIcons.FaBalanceScale
                  className="me-2"
                  style={{ fontSize: "1.2em" }}
                />
                Administración y Contabilidad
              </span>
              {submenuOpen.contabilidad ? (
                <FaIcons.FaCaretDown />
              ) : (
                <FaIcons.FaCaretUp />
              )}
            </div>
            <ul
              className="list-unstyled ms-4 mt-1"
              style={getSubMenuStyle(submenuOpen.contabilidad)}
            >
              <li>
                <NavLink
                  to="/gestion-contable"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/gestion-contable")}
                >
                  <FaIcons.FaTachometerAlt className="me-1" /> Dashboard Financiero
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/diario"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/diario")}
                >
                  <FaIcons.FaBookOpen className="me-1" /> Libro Diario
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/mayor"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/mayor")}
                >
                  <FaIcons.FaBook className="me-1" /> Libro Mayor
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/balance-comprobacion"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/balance-comprobacion")}
                >
                  <FaIcons.FaBalanceScale className="me-1" /> Balance Comprobación
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/estados-financieros"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/estados-financieros")}
                >
                  <FaIcons.FaChartBar className="me-1" /> Estados Financieros
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/analisis-contable"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/analisis-contable")}
                >
                  <FaIcons.FaSearchDollar className="me-1" /> Análisis Contable
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/libros"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/libros")}
                >
                  <FaIcons.FaClipboardList className="me-1" /> Libros Electrónicos (PLE)
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/pcge"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/pcge")}
                >
                  <FaIcons.FaListOl className="me-1" /> Plan de Cuentas (PCGE)
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contabilidad/cierres"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/contabilidad/cierres")}
                >
                  <FaIcons.FaLock className="me-1" /> Cierres Contables
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/config-contable"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/config-contable")}
                >
                  <FaIcons.FaCog className="me-1" /> Configuración
                </NavLink>
              </li>
            </ul>
          </li>
        )}


        {/* Gestión de Asistencia */}
        {tieneAccesoAsistencia && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("asistencia")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("asistencia"),
                  border:
                    getActiveCategory() === "asistencia"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "asistencia"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("asistencia"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaUserClock className="me-2" style={{ fontSize: "1.2em" }} />
                  Gestión de Asistencia
                </span>
                {submenuOpen.asistencia ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.asistencia)}
              >
                {checkPermission(usuario, "asistencia", "APROBACIONES") && (
                  <li>
                    <NavLink
                      to="/asistencia/aprobaciones"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/asistencia/aprobaciones")}
                    >
                      <FaIcons.FaCheckCircle className="me-1" /> Aprobaciones de
                      Asistencia
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "REPORTE_PDF") && (
                  <li>
                    <NavLink
                      to="/asistenciaPDF"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/asistenciaPDF")}
                    >
                      <FaIcons.FaFilePdf className="me-1" /> Reportes de
                      Asistencia PDF
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "COORDENADAS") && (
                  <li>
                    <NavLink
                      to="/coordenadas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/coordenadas")}
                    >
                      <FaMapMarkerAlt className="me-1" /> Registro de Coordenadas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "CONFIGURACION") && (
                  <li>
                    <NavLink
                      to="/config-asistencia"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/config-asistencia")}
                    >
                      <FaIcons.FaCog className="me-1" /> Configuración de
                      Asistencia
                    </NavLink>
                  </li>
                )}
                {puedeGenerarCodigosTelegram(usuario) && (
                  <li>
                    <NavLink
                      to="/asistencia/telegram"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/asistencia/telegram")}
                    >
                      <FaIcons.FaTelegram className="me-1" /> Telegram
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "OCURRENCIAS") && (
                  <li>
                    <NavLink
                      to="/ocurrencias"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ocurrencias")}
                    >
                      <FaCalendarCheck className="me-1" /> Ocurrencias
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "REGIMEN_TRABAJO") && (
                  <li>
                    <NavLink
                      to="/regimen-trabajo"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/regimen-trabajo")}
                    >
                      <FaIcons.FaClock className="me-1" /> Regimen de Trabajo
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "ROL_TURNOS") && (
                  <li>
                    <NavLink
                      to="/rol-turnos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/rol-turnos")}
                    >
                      <FaIcons.FaCalendarAlt className="me-1" /> Rol de Turnos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "EVENTOS") && (
                  <li>
                    <NavLink
                      to="/eventos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/eventos")}
                    >
                      <FaIcons.FaCalendarDay className="me-1" /> Eventos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "REPORTES") && (
                  <li>
                    <NavLink
                      to="/reportes-asistencia"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/reportes-asistencia")}
                    >
                      <FaIcons.FaChartBar className="me-1" /> Reportes de
                      Asistencia
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "INGRESOS") && (
                  <li>
                    <NavLink
                      to="/reporte-ingresos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/reporte-ingresos")}
                    >
                      <FaIcons.FaChartBar className="me-1" /> Reportes de
                      Servicios
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "TRANSPORTE") && (
                  <li>
                    <NavLink
                      to="/gestion-transporte"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/gestion-transporte")}
                    >
                      <FaIcons.FaBus className="me-1" /> Buses y Rutas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "asistencia", "TRAZABILIDAD") && (
                  <li>
                    <NavLink
                      to="/reporte-trazabilidad"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/reporte-trazabilidad")}
                    >
                      <FaIcons.FaMapMarkedAlt className="me-1" /> Trazabilidad GPS
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Gestión de Ausencias */}
        {tieneAccesoAusencias && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("ausencias")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("ausencias"),
                  border:
                    getActiveCategory() === "ausencias"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "ausencias"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("ausencias"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaRegCalendarAlt
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Ausencias
                </span>
                {submenuOpen.ausencias ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.ausencias)}
              >
                {checkPermission(usuario, "ausencias", "DASHBOARD") && (
                  <li>
                    <NavLink
                      to="/ausencias/dashboard"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/dashboard")}
                    >
                      <FaIcons.FaTachometerAlt className="me-1" /> Dashboard Ejecutivo
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "ALERTAS") && (
                  <li>
                    <NavLink
                      to="/ausencias/alertas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/alertas")}
                    >
                      <FaIcons.FaBell className="me-1" /> Alertas de Vacaciones
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "IMPACTO") && (
                  <li>
                    <NavLink
                      to="/ausencias/impacto-financiero"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/impacto-financiero")}
                    >
                      <FaIcons.FaMoneyBillWave className="me-1" /> Impacto Financiero
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "DETALLE_AUSENCIAS") && (
                  <li>
                    <NavLink
                      to="/ausencias/ausencias-detalle"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/ausencias-detalle")}
                    >
                      <FaIcons.FaSearchLocation className="me-1" /> Detalle de
                      Ausencias
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "AUSENTES_MES") && (
                  <li>
                    <NavLink
                      to="/ausencias/ausentes-mes"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/ausentes-mes")}
                    >
                      <FaIcons.FaUsers className="me-1" /> Trabajadores Ausentes
                      del Mes
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "INDICADORES") && (
                  <li>
                    <NavLink
                      to="/ausencias/indicadores"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/indicadores")}
                    >
                      <FaIcons.FaChartBar className="me-1" /> Indicadores de
                      Ausentismo
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "VACACIONES_DETALLE") && (
                  <li>
                    <NavLink
                      to="/ausencias/vacaciones-detalle"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/vacaciones-detalle")}
                    >
                      <FaIcons.FaSearch className="me-1" /> Detalle de Vacaciones
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "VACACIONES_MASIVAS") && (
                  <li>
                    <NavLink
                      to="/ausencias/vacaciones-masivas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/vacaciones-masivas")}
                    >
                      <FaIcons.FaUpload className="me-1" /> Vacaciones Masivas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "ADMIN") && (
                  <li>
                    <NavLink
                      to="/ausencias/admin"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/admin")}
                    >
                      <FaIcons.FaInbox className="me-1" /> Bandeja Admin
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "CONTROL_PAPELETAS") && (
                  <li>
                    <NavLink
                      to="/ausencias/control-papeletas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/control-papeletas")}
                    >
                      <FaIcons.FaSignature className="me-1" /> Control de Papeletas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "CALENDARIO") && (
                  <li>
                    <NavLink
                      to="/ausencias/calendario"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/calendario")}
                    >
                      <FaIcons.FaCalendarAlt className="me-1" /> Calendario
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "SOLICITUD") && (
                  <li>
                    <NavLink
                      to="/ausencias/solicitud"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/solicitud")}
                    >
                      <FaIcons.FaCalendarPlus className="me-1" /> Nueva Solicitud
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "ausencias", "INCREMENTO") && (
                  <li>
                    <NavLink
                      to="/ausencias/incremento-automatico"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ausencias/incremento-automatico")}
                    >
                      <FaIcons.FaLayerGroup className="me-1" /> Incremento Automático
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Gestión de Equipos y EPPs */}
        {tieneAccesoEpps && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("epps")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("epps"),
                  border:
                    getActiveCategory() === "epps"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "epps"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("epps"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaHardHat
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Equipos y EPPs
                </span>
                {submenuOpen.epps ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.epps)}
              >
                {checkPermission(usuario, "epps", "CATALOGO_EPPS") && (
                  <li>
                    <NavLink
                      to="/catalogo_epps"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/catalogo_epps")}
                    >
                      <FaIcons.FaShieldAlt className="me-1" /> Catálogo EPPs
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "epps", "CATALOGO_EQUIPOS") && (
                  <li>
                    <NavLink
                      to="/catalogo_equipos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/catalogo_equipos")}
                    >
                      <FaIcons.FaCogs className="me-1" /> Catálogo Equipos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "epps", "CATALOGO_HERRAMIENTAS") && (
                  <li>
                    <NavLink
                      to="/catalogo_herramientas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/catalogo_herramientas")}
                    >
                      <FaIcons.FaTools className="me-1" /> Catálogo Herramientas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "epps", "ASIGNAR") && (
                  <li>
                    <NavLink
                      to="/activos/entrega"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/activos/entrega")}
                    >
                      <FaIcons.FaUserEdit className="me-1" /> Entrega de Activos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "epps", "ESTADISTICA") && (
                  <li>
                    <NavLink
                      to="/estadistica_epps"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/estadistica_epps")}
                    >
                      <FaIcons.FaChartPie className="me-1" /> Indicadores EPPs
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "epps", "PRESTAMOS") && (
                  <li>
                    <NavLink
                      to="/prestamos_activos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/prestamos_activos")}
                    >
                      <FaIcons.FaExchangeAlt className="me-1" /> Préstamos y Devoluciones
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Gestión de Reclutamiento y Selección */}
        {tieneAccesoRecruiting && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("recruiting")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("recruiting"),
                  border:
                    getActiveCategory() === "recruiting"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "recruiting"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("recruiting"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaUserTie
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Reclutamiento y Selección
                </span>
                {submenuOpen.recruiting ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.recruiting)}
              >
                {checkPermission(usuario, "recruiting", "CANDIDATOS") && (
                  <li>
                    <NavLink
                      to="/candidatos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/candidatos")}
                    >
                      <FaIcons.FaUsers className="me-1" /> Banco de Candidatos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "recruiting", "CATALOGOS") && (
                  <li>
                    <NavLink
                      to="/catalogos/areas"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/catalogos/areas")}
                    >
                      <FaIcons.FaDatabase className="me-1" /> Catálogos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "recruiting", "UPLOAD_CV") && (
                  <li>
                    <NavLink
                      to="/CurriculumUpload"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/CurriculumUpload")}
                    >
                      <FaIcons.FaFileUpload className="me-1" /> Procesamiento de
                      CVs
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "recruiting", "CREAR_VACANTE") && (
                  <li>
                    <NavLink
                      to="/vacantes/crear"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/vacantes/crear")}
                    >
                      <FaIcons.FaPlus className="me-1" /> Crear Vacante
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "recruiting", "GESTION_VACANTES") && (
                  <li>
                    <NavLink
                      to="/vacantes"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/vacantes")}
                    >
                      <FaIcons.FaBriefcase className="me-1" /> Gestión de Vacantes
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Gestión de onboarding */}
        {tieneAccesoOnboarding && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("onboarding")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("onboarding"),
                  border:
                    getActiveCategory() === "onboarding"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "onboarding"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("onboarding"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaRegCalendarAlt
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Onboarding
                </span>
                {submenuOpen.onboarding ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.onboarding)}
              >
                {checkPermission(usuario, "onboarding", "CONFIG_DOCS") && (
                  <li>
                    <NavLink
                      to="/ConfiguracionDocumentos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ConfiguracionDocumentos")}
                    >
                      <FaIcons.FaCog className="me-1" /> Configuración de
                      Documentos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "DJ") && (
                  <li>
                    <NavLink
                      to="/dj"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/dj")}
                    >
                      <FaIcons.FaGavel className="me-1" /> DJ
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "ESTADISTICAS") && (
                  <li>
                    <NavLink
                      to="/ListaDocumentosDetalle"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/ListaDocumentosDetalle")}
                    >
                      <FaIcons.FaChartBar className="me-1" /> Estadísticas
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "LISTA_MAESTRA") && (
                  <>
                    <li>
                      <NavLink
                        to="/ListaMaestra"
                        className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                        style={getSubmenuLinkStyle("/ListaMaestra")}
                      >
                        <FaIcons.FaClipboardList className="me-1" /> Lista Maestra
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/onboarding/editor-cabecera"
                        className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                        style={getSubmenuLinkStyle("/onboarding/editor-cabecera")}
                      >
                        <FaIcons.FaCog className="me-1" /> Editor Cabecera SGI
                      </NavLink>
                    </li>
                  </>
                )}
                {checkPermission(usuario, "onboarding", "DOCUMENTOS") && (
                  <li>
                    <NavLink
                      to="/onboarding"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/onboarding")}
                    >
                      <FaIcons.FaFileAlt className="me-1" /> Documentos Onboarding
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "SEGUIMIENTO") && (
                  <li>
                    <NavLink
                      to="/SeguimientoOnboarding"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/SeguimientoOnboarding")}
                    >
                      <FaIcons.FaEye className="me-1" /> Seguimiento Onboarding
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "DESCARGAR_DOCUMENTOS_ONBOARDING") && (
                  <li>
                    <NavLink
                      to="/onboarding/descargar-documentos"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/onboarding/descargar-documentos")}
                    >
                      <FaIcons.FaFileDownload className="me-1" /> Descargar Documentos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "SOLICITUD") && (
                  <li>
                    <NavLink
                      to="/solicitud"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/solicitud")}
                    >
                      <FaIcons.FaFileMedical className="me-1" /> Solicitud
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "MEDICO_OCUPACIONAL") && (
                  <li>
                    <NavLink
                      to="/onboarding/gestion-medica"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/onboarding/gestion-medica")}
                    >
                      <FaIcons.FaUserMd className="me-1" /> Gestión Médica
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "MEDICO_OCUPACIONAL") && (
                  <li>
                    <NavLink
                      to="/onboarding/enviar-examen-medico"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/onboarding/enviar-examen-medico")}
                    >
                      <FaIcons.FaUpload className="me-1" /> Enviar Exámenes Médicos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "onboarding", "FICHA_PERSONAL") && (
                  <li>
                    <NavLink
                      to="/onboarding/ficha-personal"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/onboarding/ficha-personal")}
                    >
                      <FaIcons.FaIdCard className="me-1" /> Ficha Personal RRHH
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}



        {/* Gestión de Manuales - Solo para SuperAdmin */}
        {esPerfilSuperAdmin && (
          <li className="nav-item mb-2">
            <div
              className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
              onClick={() => toggleSubmenu("manuales")}
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
                background: getCategoryHighlight("manuales"),
                border:
                  getActiveCategory() === "manuales"
                    ? "1px solid rgba(74, 158, 255, 0.3)"
                    : "1px solid transparent",
              }}
              onMouseOver={(e) =>
              (e.currentTarget.style.background =
                getActiveCategory() === "manuales"
                  ? "rgba(74, 158, 255, 0.3)"
                  : "rgba(255, 255, 255, 0.1)")
              }
              onMouseOut={(e) =>
              (e.currentTarget.style.background =
                getCategoryHighlight("manuales"))
              }
            >
              <span className="d-flex align-items-center">
                <FaIcons.FaBook
                  className="me-2"
                  style={{ fontSize: "1.2em" }}
                />
                Gestión de Manuales
              </span>
              {submenuOpen.manuales ? (
                <FaIcons.FaCaretDown />
              ) : (
                <FaIcons.FaCaretUp />
              )}
            </div>
            <ul
              className="list-unstyled ms-4 mt-1"
              style={getSubMenuStyle(submenuOpen.manuales)}
            >
              <li>
                <NavLink
                  to="/docs"
                  className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                  style={getSubmenuLinkStyle("/docs")}
                >
                  <FaIcons.FaBookOpen className="me-1" /> Gestor de Manuales
                </NavLink>
              </li>
            </ul>
          </li>
        )}

        {/* Agentes AI */}
        {tieneAccesoAgentes && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("agentes")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("agentes"),
                  border:
                    getActiveCategory() === "agentes"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "agentes"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("agentes"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaRobot
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Agentes AI
                </span>
                {submenuOpen.agentes ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.agentes)}
              >
                {checkPermission(usuario, "agentes", "PROCESADOR") && (
                  <li>
                    <NavLink
                      to="/documentos/procesador"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/procesador")}
                    >
                      <FaIcons.FaBrain className="me-1" /> Procesador de
                      Documentos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "agentes", "HIRO_CV") && (
                  <li>
                    <NavLink
                      to="/documentos/procesador/hiro"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/procesador/hiro")}
                    >
                      <FaIcons.FaUserTie className="me-1" /> Hiro - Procesador de
                      CVs
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "agentes", "HIRO_CHAT") && (
                  <li>
                    <NavLink
                      to="/documentos/procesador/chat-hiro"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/procesador/chat-hiro")}
                    >
                      <FaIcons.FaUserTie className="me-1" /> Hiro - Chat
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "agentes", "VEXA") && (
                  <li>
                    <NavLink
                      to="/documentos/procesador/vexa"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/procesador/vexa")}
                    >
                      <FaIcons.FaMoneyBillWave className="me-1" /> Vexa -
                      Cotizaciones
                    </NavLink>
                  </li>
                )}
                <li>
                  <NavLink
                    to="/documentos/procesador/chat-axis"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/documentos/procesador/chat-axis")}
                  >
                    <FaIcons.FaRoute className="me-1" /> Axis - Orquestador
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/documentos/procesador/chat-axio"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/documentos/procesador/chat-axio")}
                  >
                    <FaIcons.FaProjectDiagram className="me-1" /> Axio - Backlog IA
                  </NavLink>
                </li>
                {checkPermission(usuario, "agentes", "ECHO_CHAT") && (
                  <li>
                    <NavLink
                      to="/documentos/procesador/chat-echo"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/documentos/procesador/chat-echo")}
                    >
                      <FaIcons.FaBullhorn className="me-1" /> Echo - Comunicaciones
                    </NavLink>
                  </li>
                )}
                <li>
                  <NavLink
                    to="/documentos/procesador/chat-nova"
                    className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                    style={getSubmenuLinkStyle("/documentos/procesador/chat-nova")}
                  >
                    <FaIcons.FaClipboardCheck className="me-1" /> Nova - Onboarding
                  </NavLink>
                </li>
              </ul>
            </li>
        )}

        {/* Gestión de Capacitaciones */}
        {tieneAccesoCapacitaciones && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("capacitaciones")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("capacitaciones"),
                  border:
                    getActiveCategory() === "capacitaciones"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "capacitaciones"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("capacitaciones"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaGraduationCap
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Gestión de Capacitaciones
                </span>
                {submenuOpen.capacitaciones ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.capacitaciones)}
              >
                {checkPermission(usuario, "capacitaciones", "DASHBOARD") && (
                  <li>
                    <NavLink
                      to="/capacitaciones/dashboard"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/capacitaciones/dashboard")}
                    >
                      <FaIcons.FaTachometerAlt className="me-1" /> Dashboard
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "capacitaciones", "DASHBOARD") && (
                  <li>
                    <NavLink
                      to="/capacitaciones/administrar"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/capacitaciones/administrar")}
                    >
                      <FaIcons.FaBook className="me-1" /> Administrar Cursos
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "capacitaciones", "USUARIOS") && (
                  <li>
                    <NavLink
                      to="/capacitaciones/usuarios"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/capacitaciones/usuarios")}
                    >
                      <FaIcons.FaUsers className="me-1" /> Usuarios
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}

        {/* Gestión de Evaluación de Desempeño */}
        {tieneAccesoEvaluaciones && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("evaluaciones")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("evaluaciones"),
                  border:
                    getActiveCategory() === "evaluaciones"
                      ? "1px solid rgba(240, 147, 251, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "evaluaciones"
                    ? "rgba(240, 147, 251, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("evaluaciones"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaClipboardCheck
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Evaluación de Desempeño
                </span>
                {submenuOpen.evaluaciones ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.evaluaciones)}
              >
                {checkPermission(usuario, "evaluaciones", "DASHBOARD") && (
                  <li>
                    <NavLink
                      to="/evaluaciones"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/evaluaciones")}
                    >
                      <FaIcons.FaTachometerAlt className="me-1" /> Dashboard
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "evaluaciones", "CREAR_CAMPANA") && (
                  <li>
                    <NavLink
                      to="/evaluaciones/crear-campana"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/evaluaciones/crear-campana")}
                    >
                      <FaIcons.FaPlus className="me-1" /> Crear Campaña
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}
        {/* Atención al Cliente y Servicios */}
        {tieneAccesoAtencion && (
            <li className="nav-item mb-2">
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 text-white rounded"
                onClick={() => toggleSubmenu("atencion")}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: getCategoryHighlight("atencion"),
                  border:
                    getActiveCategory() === "atencion"
                      ? "1px solid rgba(74, 158, 255, 0.3)"
                      : "1px solid transparent",
                }}
                onMouseOver={(e) =>
                (e.currentTarget.style.background =
                  getActiveCategory() === "atencion"
                    ? "rgba(74, 158, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)")
                }
                onMouseOut={(e) =>
                (e.currentTarget.style.background =
                  getCategoryHighlight("atencion"))
                }
              >
                <span className="d-flex align-items-center">
                  <FaIcons.FaLifeRing
                    className="me-2"
                    style={{ fontSize: "1.2em" }}
                  />
                  Atención al Cliente
                </span>
                {submenuOpen.atencion ? (
                  <FaIcons.FaCaretDown />
                ) : (
                  <FaIcons.FaCaretUp />
                )}
              </div>
              <ul
                className="list-unstyled ms-4 mt-1"
                style={getSubMenuStyle(submenuOpen.atencion)}
              >
                {checkPermission(usuario, "atencion", "HELP_DESK") && (
                  <li>
                    <NavLink
                      to="/atencion/mesa-ayuda"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/atencion/mesa-ayuda")}
                    >
                      <FaIcons.FaHeadset className="me-1" /> Mesa de Ayuda
                    </NavLink>
                  </li>
                )}
                {checkPermission(usuario, "atencion", "RECLAMACIONES") && (
                  <li>
                    <NavLink
                      to="/atencion/reclamaciones"
                      className={`d-block py-2 px-3 text-decoration-none border-start border-secondary ${activeClassName}`}
                      style={getSubmenuLinkStyle("/atencion/reclamaciones")}
                    >
                      <FaIcons.FaBook className="me-1" /> Libro de Reclamaciones
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
        )}
      </ul>

      {/* Estilos CSS personalizados mejorados */}
      <style jsx="true">{`
        .sidebar {
          -webkit-overflow-scrolling: touch;
          border-right: 1px solid rgba(255, 255, 255, 0.08);
        }

        .sidebar::-webkit-scrollbar {
          width: 8px;
        }

        .sidebar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.22);
          border-radius: 12px;
        }

        .sidebar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.32);
          border-radius: 12px;
        }

        .sidebar-header {
          position: sticky;
          top: -20px;
          z-index: 4;
          margin: -20px -20px 12px -20px;
          padding: 16px 20px 14px;
          background: linear-gradient(180deg, rgba(16, 16, 16, 0.98) 0%, rgba(26, 26, 26, 0.93) 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(5px);
        }

        .sidebar-eyebrow {
          color: #b8c4d6;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 700;
        }

        .sidebar-action-btn {
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(74, 158, 255, 0.15);
          color: #ffffff;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .sidebar-action-btn:hover {
          background: rgba(74, 158, 255, 0.28);
          border-color: rgba(142, 185, 255, 0.7);
        }

        .sidebar-action-btn:focus-visible {
          outline: 2px solid #8eb9ff;
          outline-offset: 2px;
        }

        .sidebar .nav-item > div {
          min-height: 42px;
          border-radius: 10px !important;
          padding-top: 10px !important;
          padding-bottom: 10px !important;
        }

        .nav-link:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .active-link {
          font-weight: 600;
          background: rgba(74, 158, 255, 0.2) !important;
          color: #ffffff !important;
        }

        .border-start:hover {
          border-left-color: #4a9eff !important;
          color: #ffffff !important;
          background: rgba(74, 158, 255, 0.1) !important;
        }

        .active-link.border-start {
          border-left-color: #4a9eff !important;
          color: #ffffff !important;
          background: rgba(74, 158, 255, 0.15) !important;
        }

        .sidebar .nav-link {
          border-radius: 8px;
        }

        .sidebar .nav-link:focus-visible {
          outline: 2px solid #8eb9ff;
          outline-offset: 1px;
        }

        .sidebar .list-unstyled .nav-link {
          line-height: 1.3;
          margin-bottom: 2px;
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
